/**
 * S20 — Vendors · Integration Hub (`/vendors/integrations`).
 * Connected vendors (with model + residency + MSA detail) + a catalogue
 * of available connectors awaiting approval.
 */
import React from "react";
import { Plug, ShieldCheck, AlertTriangle, ServerCog, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

export default function IntegrationHub() {
  const { user } = useAuth();
  // Phase 3b — primary vendor list from REST; vendorDetails +
  // vendorCatalogue stay on the bundle (precomputed seed fields, no
  // dedicated endpoints yet).
  const { items: vendors, loading, error, lastSyncedAt, refetch } = useEntityList("/api/vendors");
  const { vendorDetails = {}, vendorCatalogue = [], isCustomer } = useTenantData();
  if (loading && vendors.length === 0) return <IHSkeleton />;
  if (error) return <IHError message={error.message} />;
  if (isCustomer && !vendors.length) return <Empty userName={user?.name?.split(" ")[0]} />;

  const healthy = vendors.filter((v) => v.status === "healthy").length;
  const degraded = vendors.filter((v) => v.status !== "healthy").length;

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="integration-hub">
      <div className="absolute -right-24 -top-12 pointer-events-none"><Flywheel size={300} opacity={0.05} /></div>

      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Vendors · Integration Hub</p>
          <h1 className="text-display-md text-ink-primary mt-1">Vendor Integrations</h1>
          <p className="text-ink-secondary mt-1 max-w-2xl">
            Every AI vendor connected to ALGOS, plus a catalogue of vendors awaiting
            risk + residency review.
          </p>
        </div>
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={loading}
          onRefetch={refetch}
          testId="integration-hub-synced"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile label="Connected vendors" value={vendors.length.toString()}  delta={`${healthy} healthy`}         deltaTone="up" hint="serving live traffic"        icon={Plug}        tone="tralenex" testId="vh-kpi-connected" />
        <KpiTile label="Degraded"          value={degraded.toString()}        delta={degraded > 0 ? "Watch" : "OK"} deltaTone={degraded > 0 ? "down" : "up"} hint="elevated latency / errors" icon={AlertTriangle} tone="risk" testId="vh-kpi-degraded" />
        <KpiTile label="Catalogue items"   value={vendorCatalogue.length.toString()} delta="awaiting review"        deltaTone="neutral" hint="vendor risk pipeline"       icon={ServerCog}   tone="gold"     testId="vh-kpi-catalogue" />
        <KpiTile label="MFA enforced"      value={`${vendors.length}/${vendors.length}`} delta="100% coverage"      deltaTone="up" hint="all vendor consoles"            icon={Lock}        tone="low"      testId="vh-kpi-mfa" />
      </div>

      <div className="mt-6 surface-card shadow-e1 overflow-hidden" data-testid="vh-connected-panel">
        <div className="px-4 py-3 border-b border-border-subtle">
          <p className="eyebrow">Connected</p>
          <p className="text-sm text-ink-secondary mt-0.5">{vendors.length} vendors actively serving traffic.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vh-connected-table">
            <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
              <tr>{["Vendor","Category","Models","Residency","Auth","Contract","Connected since","Status"].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {vendors.map((v) => {
                const d = vendorDetails[v.id] || {};
                return (
                  <tr key={v.id} className="border-t border-border-subtle hover:bg-inset/30" data-testid={`vh-row-${v.id}`}>
                    <td className="px-3 py-2 text-ink-primary font-medium whitespace-nowrap">{v.name}</td>
                    <td className="px-3 py-2 text-ink-secondary whitespace-nowrap">{v.category}</td>
                    <td className="px-3 py-2 text-meta text-ink-secondary">{(d.models || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">{d.residency || "—"}</td>
                    <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">{v.authMethod}</td>
                    <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">{d.contractTier || "—"}</td>
                    <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">{d.connectedSince || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.06em]",
                        v.status === "healthy" ? "bg-risk-low/10 text-risk-low" : "bg-gold-300/15 text-gold-700")}>{v.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 surface-card shadow-e1 overflow-hidden" data-testid="vh-catalogue-panel">
        <div className="px-4 py-3 border-b border-border-subtle">
          <p className="eyebrow">Catalogue · Available</p>
          <p className="text-sm text-ink-secondary mt-0.5">Vendors not yet installed — each carries a residency / risk decision.</p>
        </div>
        <ul className="divide-y divide-border-subtle">
          {vendorCatalogue.map((c) => (
            <li key={c.id} className="px-4 py-3 flex items-start gap-4" data-testid={`vh-cat-${c.id}`}>
              <span className={cn("mt-1 w-2 h-2 rounded-full shrink-0",
                c.availability === "available" ? "bg-risk-low" : c.availability === "restricted" ? "bg-gold-600" : "bg-risk-high")} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="text-ink-primary font-medium">{c.name}</p>
                  <p className="text-meta text-ink-secondary">{c.category} · {c.residency} · MSA {c.msa}</p>
                </div>
                <p className="text-sm text-ink-primary mt-0.5 leading-snug">{c.reason}</p>
              </div>
              <span className="text-meta uppercase tracking-[0.06em] font-bold text-ink-secondary shrink-0">{c.availability}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Empty({ userName }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden" data-testid="vh-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none"><Flywheel size={620} opacity={0.04} /></div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Vendors · Integration Hub</p>
        <h1 className="text-display-lg text-ink-primary mt-1">{userName ? `Welcome, ${userName}.` : "Welcome."}</h1>
        <p className="text-ink-secondary mt-3 text-[15px]">No vendors connected. Add your first LLM provider to bring your AI estate under governance.</p>
      </div>
    </div>
  );
}

function IHSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="integration-hub-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[300px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function IHError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="integration-hub-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Vendors · Integration Hub</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load vendor integrations</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

